'use client';

/**
 * Host access dialog behind `host.files.request()` — the consent chrome for
 * the grant-gated `files.*` bridge family (the viewer's library). Standard
 * portal chrome rendered OVER the canvas (same trust posture as the
 * save/pick dialogs): the widget only learns the decision, never drives it.
 *
 * Opened via `CustomEvent('widget-files-access')` whose detail carries
 * `{ widgetName, resolve }`. The user either allows the ENTIRE LIBRARY
 * (default action), picks ONE FOLDER through the shared PortalFileDialog
 * (`move` mode = pure folder picker), or declines. Resolves
 * `{ scope: 'all' | <folderId> } | null`; the BRIDGE writes the grant
 * (per-user localStorage, `lib/workspaces/widget-permissions-local.ts`).
 */

import { useCallback, useEffect, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import { FolderLock, FolderSearch, Library, X } from 'lucide-react';
import { PortalFileDialog } from '@/components/workspace/portal-file-dialog';
import { useAppFrameFocusReturn } from '@/components/workspace/app-frame-focus-return';

interface AccessRequest {
  widgetName: string;
  resolve: (result: { scope: 'all' | string } | null) => void;
}

export function WidgetFilesAccessDialog() {
  const t = useT();
  const [req, setReq] = useState<AccessRequest | null>(null);
  // Close hands focus back to the app that asked (app-frame-focus-return.ts).
  const captureAppFocus = useAppFrameFocusReturn(!!req);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<AccessRequest>).detail;
      if (!detail || typeof detail.resolve !== 'function') return;
      captureAppFocus();
      setReq(detail);
      setPickerOpen(false);
    };
    window.addEventListener('widget-files-access', onOpen);
    return () => window.removeEventListener('widget-files-access', onOpen);
  }, [captureAppFocus]);

  const settle = useCallback(
    (result: { scope: 'all' | string } | null) => {
      if (!req) return;
      req.resolve(result);
      setReq(null);
      setPickerOpen(false);
    },
    [req],
  );

  useEffect(() => {
    if (!req) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !pickerOpen) settle(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [req, pickerOpen, settle]);

  if (!req) return null;

  if (pickerOpen) {
    return (
      <PortalFileDialog
        mode="move"
        title={t('workspace.filesAccess.allowOneFolder')}
        onResolve={(result) => {
          if (!result) {
            // Back out of the picker, keep the main dialog open.
            setPickerOpen(false);
            return;
          }
          // Picking the root in the folder browser IS the entire library.
          settle({ scope: result.parentId === null ? 'all' : result.parentId });
        }}
      />
    );
  }

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 backdrop-blur-[2px]"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) settle(null);
      }}
    >
      <div className="w-[440px] max-w-[calc(100vw-32px)] rounded-2xl border border-border bg-background p-5 shadow-2xl">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <FolderLock className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold leading-snug">
              Allow “{req.widgetName}” to access your files?
            </h3>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              {t('workspace.filesAccess.scopeNote')}
            </p>
          </div>
          <button
            type="button"
            aria-label={t('push.notNow')}
            onClick={() => settle(null)}
            className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-4 flex flex-col gap-2">
          <button
            type="button"
            onClick={() => settle({ scope: 'all' })}
            className="flex items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 px-3.5 py-2.5 text-left transition-colors hover:bg-primary/10"
          >
            <Library className="h-4 w-4 shrink-0 text-primary" />
            <span className="min-w-0">
              <span className="block text-[13px] font-medium">{t('workspace.filesAccess.entireLibrary')}</span>
              <span className="block text-[11.5px] text-muted-foreground">
                {t('workspace.filesAccess.entireLibraryDesc')}
              </span>
            </span>
          </button>
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="flex items-center gap-3 rounded-xl border border-border px-3.5 py-2.5 text-left transition-colors hover:bg-muted/60"
          >
            <FolderSearch className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="min-w-0">
              <span className="block text-[13px] font-medium">{t('workspace.filesAccess.oneFolderOnly')}</span>
              <span className="block text-[11.5px] text-muted-foreground">
                Pick a folder; the app sees nothing outside it
              </span>
            </span>
          </button>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={() => settle(null)}
            className="rounded-lg px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            {t('push.notNow')}
          </button>
        </div>
      </div>
    </div>
  );
}
